import { useCallback, useState, useEffect, useRef } from 'react';
import { useAuth } from '@/auth';
import { usePreferences } from '@/auth/hooks/usePreferences';
import { useFiltersStore } from '@/store/filtersStore';
import { PasseriniAPI } from '@/services/apiService';
import {
  buildPersistableFilters,
  buildTramitesUrl,
  getDefaultFilters,
  getInitialFilters,
} from './utils';
import type { PersistableTramiteFilters, ApplyFiltersParams } from './types';

interface PaginationLink {
  url: string | null;
  label: string;
  active: boolean;
  onClick: () => void;
}

interface PaginationData {
  current_page: number;
  from: number;
  to: number;
  total: number;
  links: Omit<PaginationLink, 'onClick'>[];
}

interface UseTramitesOptions {
  /**
   * Permite suspender las requests del listado paginado (por ejemplo en la vista
   * kanban, donde cada columna trae sus propios datos). Los filtros se siguen
   * hidratando y persistiendo igual.
   */
  enabled?: boolean;
}

export function useTramites(
  userPermissions: string[] = [],
  { enabled = true }: UseTramitesOptions = {},
) {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const {
    preferences,
    updatePartialPreference,
    isLoading: preferencesLoading,
    isError: preferencesFailed,
  } = usePreferences();
  const {
    filters: storeFilters,
    isInitialized: isStoreInitialized,
    lastUserId,
    initialize: initializeStore,
    setFilters: setStoreFilters,
    replaceFilters: replaceStoreFilters,
    resetToDefaults: resetStoreToDefaults,
    clear: clearStore,
  } = useFiltersStore();

  /**
   * 'pending'  → todavía no se sincronizaron los filtros
   * 'defaults' → se arrancó con los valores por defecto porque las preferencias
   *              fallaron; si un reintento funciona, se vuelve a sincronizar
   * 'server'   → se aplicaron las preferencias reales del usuario, definitivo
   *
   * Antes esto era un booleano que se ponía en true aunque las preferencias
   * hubieran fallado: el usuario perdía sus filtros y su vista kanban para toda
   * la sesión, sin ningún aviso.
   */
  const preferencesSyncState = useRef<'pending' | 'defaults' | 'server'>('pending');
  const hasMounted = useRef(false);
  const activeRequestIdRef = useRef(0);
  const abortControllerRef = useRef<AbortController | null>(null);
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  const defaults = getDefaultFilters(userPermissions);

  const [tramites, setTramites] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [links, setLinks] = useState<PaginationLink[]>([]);
  const [currentPage, setCurrentPage] = useState<number | undefined>(undefined);
  const [totalItems, setTotalItems] = useState<number | undefined>(undefined);
  const [from, setFrom] = useState<number | undefined>(undefined);
  const [to, setTo] = useState<number | undefined>(undefined);
  const [isReady, setIsReady] = useState(false);

  const [searchText, setSearchText] = useState(defaults.searchText);
  const [activeSearchText, setActiveSearchText] = useState(defaults.activeSearchText);
  const [sortField, setSortField] = useState(defaults.sortField);
  const [sortDirection, setSortDirection] = useState(defaults.sortDirection);
  const [dateFrom, setDateFrom] = useState(defaults.dateFrom);
  const [dateTo, setDateTo] = useState(defaults.dateTo);
  const [dateField, setDateField] = useState(defaults.dateField);
  const [escrituraFilter, setEscrituraFilter] = useState(defaults.escrituraFilter);
  const [registroFilter, setRegistroFilter] = useState(defaults.registroFilter);
  const [estadoFilter, setEstadoFilter] = useState(defaults.estadoFilter);
  const [bloqueadaFilter, setBloqueadaFilter] = useState(defaults.bloqueadaFilter);
  const [archivadoFilter, setArchivadoFilter] = useState(defaults.archivadoFilter);
  const [assignedUserId, setAssignedUserId] = useState(defaults.assignedUserId);
  const [assignedUser, setAssignedUser] = useState(defaults.assignedUser);

  const applyPersistableFiltersToState = useCallback((filters: PersistableTramiteFilters) => {
    setSearchText(filters.activeSearchText);
    setActiveSearchText(filters.activeSearchText);
    setSortField(filters.sortField);
    setSortDirection(filters.sortDirection);
    setDateFrom(filters.dateFrom);
    setDateTo(filters.dateTo);
    setDateField(filters.dateField);
    setEscrituraFilter(filters.escrituraFilter);
    setRegistroFilter(filters.registroFilter);
    setEstadoFilter(filters.estadoFilter);
    setBloqueadaFilter(filters.bloqueadaFilter);
    setArchivadoFilter(filters.archivadoFilter);
    setAssignedUserId(filters.assignedUserId);
    setAssignedUser(filters.assignedUser);
  }, []);

  // Ref to always have latest filter values for the sort persistence effect
  const filtersRef = useRef<PersistableTramiteFilters>({
    activeSearchText: defaults.activeSearchText,
    sortField: defaults.sortField,
    sortDirection: defaults.sortDirection,
    dateFrom: defaults.dateFrom,
    dateTo: defaults.dateTo,
    dateField: defaults.dateField,
    escrituraFilter: defaults.escrituraFilter,
    registroFilter: defaults.registroFilter,
    estadoFilter: defaults.estadoFilter,
    bloqueadaFilter: defaults.bloqueadaFilter,
    archivadoFilter: defaults.archivadoFilter,
    assignedUserId: defaults.assignedUserId,
    assignedUser: defaults.assignedUser,
  });

  filtersRef.current = {
    activeSearchText,
    sortField,
    sortDirection,
    dateFrom,
    dateTo,
    dateField,
    escrituraFilter,
    registroFilter,
    estadoFilter,
    bloqueadaFilter,
    archivadoFilter,
    assignedUserId,
    assignedUser,
  };

  // Reset in-memory store when auth user changes to avoid cross-user leakage
  useEffect(() => {
    if (lastUserId !== null && lastUserId !== userId) {
      clearStore();
      preferencesSyncState.current = 'pending';
      setIsReady(false);
    }
  }, [lastUserId, userId, clearStore]);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const isAbortError = (error: any) => {
    return error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED';
  };

  const fetchTramitesSafely = useCallback(async (url: string) => {
    if (!enabledRef.current) return;

    activeRequestIdRef.current += 1;
    const requestId = activeRequestIdRef.current;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;
    setLoading(true);

    try {
      const response = await PasseriniAPI.get(url, { signal: controller.signal });
      if (requestId !== activeRequestIdRef.current) return;

      const responseData = response.data as any;
      const rows = Array.isArray(responseData?.data) ? responseData.data : [];
      const paginationValues = responseData as PaginationData;
      const newLinks = (paginationValues.links || []).map((link) => ({
        ...link,
        onClick: () => {
          if (link.url) {
            void fetchTramitesSafely(link.url);
          }
        },
      }));

      setTramites(rows);
      setLinks(newLinks);
      setCurrentPage(paginationValues.current_page);
      setTotalItems(paginationValues.total);
      setFrom(paginationValues.from);
      setTo(paginationValues.to);
    } catch (error) {
      if (requestId !== activeRequestIdRef.current) return;
      if (isAbortError(error)) return;

      setTramites([]);
      setLinks([]);
      setCurrentPage(undefined);
      setTotalItems(undefined);
      setFrom(undefined);
      setTo(undefined);
    } finally {
      if (requestId === activeRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, []);

  // Sync from preferences when they finish loading
  useEffect(() => {
    if (preferencesSyncState.current === 'server') return;
    if (preferencesLoading) return;

    const hasServerPreferences = !preferencesFailed;

    // Ya se arrancó con defaults y las preferencias siguen sin poder cargarse:
    // no tiene sentido reaplicarlas y pisar lo que el usuario haya tocado.
    if (preferencesSyncState.current === 'defaults' && !hasServerPreferences) return;

    const canHydrateFromStore = isStoreInitialized && lastUserId === userId;
    const initialPersistableFilters = canHydrateFromStore
      ? storeFilters
      : buildPersistableFilters(getInitialFilters(userPermissions, preferences));

    applyPersistableFiltersToState(initialPersistableFilters);

    if (!canHydrateFromStore) {
      initializeStore(initialPersistableFilters, userId);
    }

    preferencesSyncState.current = hasServerPreferences ? 'server' : 'defaults';
    setIsReady(true);
  }, [
    preferencesLoading,
    preferencesFailed,
    preferences,
    userPermissions,
    isStoreInitialized,
    lastUserId,
    userId,
    storeFilters,
    initializeStore,
    applyPersistableFiltersToState,
  ]);

  // Helper: persist filters to DB via partial update
  // Uses a stable ref so effects don't re-fire when the mutation state changes
  const saveFiltersRef = useRef((filters: PersistableTramiteFilters) => {
    replaceStoreFilters(filters);
    updatePartialPreference('tramites.filters', filters);
  });
  saveFiltersRef.current = (filters: PersistableTramiteFilters) => {
    replaceStoreFilters(filters);
    updatePartialPreference('tramites.filters', filters);
  };

  const saveFilters = useCallback(
    (filters: PersistableTramiteFilters) => {
      saveFiltersRef.current(filters);
    },
    [],
  );

  // Persist sort changes (skip initial mount)
  useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true;
      return;
    }
    saveFiltersRef.current(filtersRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortField, sortDirection]);

  const fetchDataTramites = useCallback(
    (searchQuery: string = activeSearchText) => {
      const url = buildTramitesUrl({
        searchQuery,
        sortField,
        sortDirection,
        dateFrom,
        dateTo,
        dateField,
        escrituraFilter,
        registroFilter,
        estadoFilter,
        bloqueadaFilter,
        archivadoFilter,
        assignedUserId,
      });
      void fetchTramitesSafely(url);
    },
    [
      activeSearchText, sortField, sortDirection, dateFrom, dateTo,
      dateField, escrituraFilter, registroFilter, estadoFilter, bloqueadaFilter, archivadoFilter, assignedUserId, fetchTramitesSafely,
    ],
  );

  const executeSearch = useCallback(() => {
    setActiveSearchText(searchText);
    fetchDataTramites(searchText);
    setStoreFilters({ activeSearchText: searchText });

    saveFilters({
      activeSearchText: searchText,
      sortField,
      sortDirection,
      dateFrom,
      dateTo,
      dateField,
      escrituraFilter,
      registroFilter,
      estadoFilter,
      bloqueadaFilter,
      archivadoFilter,
      assignedUserId,
      assignedUser,
    });
  }, [
    searchText, fetchDataTramites, saveFilters, sortField, sortDirection,
    dateFrom, dateTo, dateField, escrituraFilter, registroFilter, estadoFilter, bloqueadaFilter, archivadoFilter, assignedUserId, assignedUser, setStoreFilters,
  ]);

  const applyFilters = useCallback(
    (filters: ApplyFiltersParams) => {
      const newDateFrom = filters.dateFrom !== undefined ? filters.dateFrom : dateFrom;
      const newDateTo = filters.dateTo !== undefined ? filters.dateTo : dateTo;
      const newDateField = filters.dateField !== undefined ? filters.dateField : dateField;
      const newEscrituraFilter = filters.escrituraFilter !== undefined ? filters.escrituraFilter : escrituraFilter;
      const newRegistroFilter = filters.registroFilter !== undefined ? filters.registroFilter : registroFilter;
      const newEstadoFilter = filters.estadoFilter !== undefined ? filters.estadoFilter : estadoFilter;
      const newBloqueadaFilter = filters.bloqueadaFilter !== undefined ? filters.bloqueadaFilter : bloqueadaFilter;
      const newArchivadoFilter = filters.archivadoFilter !== undefined ? filters.archivadoFilter : archivadoFilter;
      const newAssignedUserId = filters.assignedUserId !== undefined ? filters.assignedUserId : assignedUserId;
      const newAssignedUser = filters.assignedUser !== undefined ? filters.assignedUser : assignedUser;

      if (filters.dateFrom !== undefined) setDateFrom(filters.dateFrom);
      if (filters.dateTo !== undefined) setDateTo(filters.dateTo);
      if (filters.dateField !== undefined) setDateField(filters.dateField);
      if (filters.escrituraFilter !== undefined) setEscrituraFilter(filters.escrituraFilter);
      if (filters.registroFilter !== undefined) setRegistroFilter(filters.registroFilter);
      if (filters.estadoFilter !== undefined) setEstadoFilter(filters.estadoFilter);
      if (filters.bloqueadaFilter !== undefined) setBloqueadaFilter(filters.bloqueadaFilter);
      if (filters.archivadoFilter !== undefined) setArchivadoFilter(filters.archivadoFilter);
      if (filters.assignedUserId !== undefined) setAssignedUserId(filters.assignedUserId);
      if (filters.assignedUser !== undefined) setAssignedUser(filters.assignedUser);

      const url = buildTramitesUrl({
        searchQuery: activeSearchText,
        sortField,
        sortDirection,
        dateFrom: newDateFrom,
        dateTo: newDateTo,
        dateField: newDateField,
        escrituraFilter: newEscrituraFilter,
        registroFilter: newRegistroFilter,
        estadoFilter: newEstadoFilter,
        bloqueadaFilter: newBloqueadaFilter,
        archivadoFilter: newArchivadoFilter,
        assignedUserId: newAssignedUserId,
      });
      void fetchTramitesSafely(url);

      saveFilters({
        activeSearchText,
        sortField,
        sortDirection,
        dateFrom: newDateFrom,
        dateTo: newDateTo,
        dateField: newDateField,
        escrituraFilter: newEscrituraFilter,
        registroFilter: newRegistroFilter,
        estadoFilter: newEstadoFilter,
        bloqueadaFilter: newBloqueadaFilter,
        archivadoFilter: newArchivadoFilter,
        assignedUserId: newAssignedUserId,
        assignedUser: newAssignedUser,
      });
    },
    [
      activeSearchText, sortField, sortDirection, dateFrom, dateTo,
      dateField, escrituraFilter, registroFilter, estadoFilter, bloqueadaFilter, archivadoFilter, assignedUserId, assignedUser, fetchTramitesSafely, saveFilters,
    ],
  );

  const resetFilters = useCallback(() => {
    const defaultFilters = getDefaultFilters(userPermissions);
    const persistableDefaultFilters = buildPersistableFilters(defaultFilters);

    setSearchText(defaultFilters.searchText);
    setActiveSearchText(defaultFilters.activeSearchText);
    setSortField(defaultFilters.sortField);
    setSortDirection(defaultFilters.sortDirection);
    setDateFrom(defaultFilters.dateFrom);
    setDateTo(defaultFilters.dateTo);
    setDateField(defaultFilters.dateField);
    setEscrituraFilter(defaultFilters.escrituraFilter);
    setRegistroFilter(defaultFilters.registroFilter);
    setEstadoFilter(defaultFilters.estadoFilter);
    setBloqueadaFilter(defaultFilters.bloqueadaFilter);
    setArchivadoFilter(defaultFilters.archivadoFilter);
    setAssignedUserId(defaultFilters.assignedUserId);
    setAssignedUser(defaultFilters.assignedUser);
    resetStoreToDefaults(persistableDefaultFilters, userId);

    const url = buildTramitesUrl({
      searchQuery: defaultFilters.activeSearchText,
      sortField: defaultFilters.sortField,
      sortDirection: defaultFilters.sortDirection,
      dateFrom: defaultFilters.dateFrom,
      dateTo: defaultFilters.dateTo,
      dateField: defaultFilters.dateField,
      escrituraFilter: defaultFilters.escrituraFilter,
      registroFilter: defaultFilters.registroFilter,
      estadoFilter: defaultFilters.estadoFilter,
      bloqueadaFilter: defaultFilters.bloqueadaFilter,
      archivadoFilter: defaultFilters.archivadoFilter,
      assignedUserId: defaultFilters.assignedUserId,
    });
    void fetchTramitesSafely(url);

    saveFilters(persistableDefaultFilters);
  }, [userPermissions, fetchTramitesSafely, saveFilters, resetStoreToDefaults, userId]);

  return {
    fetchDataTramites,
    executeSearch,
    applyFilters,
    resetFilters,
    isReady,
    setSearchText,
    searchText,
    activeSearchText,
    tramites,
    loading,
    links,
    currentPage,
    totalItems,
    from,
    to,
    sortField,
    sortDirection,
    setSortField,
    setSortDirection,
    dateFrom,
    dateTo,
    dateField,
    setDateFrom,
    setDateTo,
    setDateField,
    escrituraFilter,
    setEscrituraFilter,
    registroFilter,
    setRegistroFilter,
    estadoFilter,
    setEstadoFilter,
    bloqueadaFilter,
    setBloqueadaFilter,
    archivadoFilter,
    setArchivadoFilter,
    assignedUserId,
    setAssignedUserId,
    assignedUser,
    setAssignedUser,
  };
}
